/**
 * RecoveryCodesDisplay
 *
 * Renders a set of recovery codes in a monospace grid with a copy-all button.
 * Codes are single-use and shown exactly once after MFA setup completes.
 *
 * Epic: MVP 1.15 (V2) — Federated Auth, Epic 4: MFA
 * Story: SCRUM-356 — Guide MFA Setup
 */

import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * @param {Object}   props
 * @param {string[]} props.codes - Array of plaintext recovery codes
 */
export default function RecoveryCodesDisplay({ codes }) {
  const [copied, setCopied] = useState(false);

  const handleCopyAll = async () => {
    const text = codes.join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {codes.map((code, idx) => (
          <code
            key={idx}
            className="block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm font-mono text-gray-800 text-center tracking-widest"
          >
            {code}
          </code>
        ))}
      </div>

      <button
        type="button"
        onClick={handleCopyAll}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        {copied ? (
          <>
            <Check size={14} className="text-green-600" />
            <span className="text-green-600">Copied!</span>
          </>
        ) : (
          <>
            <Copy size={14} />
            Copy all codes
          </>
        )}
      </button>
    </div>
  );
}
